import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStoredTimeRange, useUrlTimeRangeOverride } from '../../../hooks/useGlobalTimeRange';
import { URL_RANGE_PARAMS, readStoredTimeRange, writeStoredTimeRange } from '../../../utils/timeRange';
import { useAtlasTimeRangeScope } from './AtlasTimeRangeScope';

/**
 * Module-wide time-range selection for Agent Atlas and Security Atlas pages.
 *
 * Backed by the same DateRangeFilter component the Gemini Enterprise pages
 * use, so the UI control matches across the product. Tracks:
 *
 *   - rangeKey  : preset key ('1d' | '7d' | '30d' | '90d' | 'custom')
 *   - dateRange : { start_time, end_time } ISO strings (used for 'custom')
 *   - days      : numeric window derived from the above (kept so existing
 *                 backend calls — which take ?days=N — keep working unchanged)
 *
 * The selection is the app-wide one in utils/timeRange (localStorage
 * `saf3ai.timeRange`), shared with the global Header filter, so moving between
 * Atlas / Inventory / Users / Agent 360 / User 360 and the rest of the product
 * keeps the same window WITHOUT carrying it in the URL. A URL that arrives
 * with a range (time_range, start_time/end_time, or a legacy days=N bookmark)
 * overrides it for that view and becomes the stored selection. Picking a range
 * here writes storage and strips any such URL override; it never adds one.
 *
 * Inside an AtlasTimeRangeScope (a module that owns its window, e.g. Gemini
 * Enterprise) the scope's window is read and written instead, and the app-wide
 * store is left alone.
 */

const DEFAULT_KEY = '90d';

// Fresh-session default (nothing stored, no URL override), route-scoped.
// Security Atlas pages sit under the global Header filter, whose default is 30
// days, so they start there and the two controls agree. Agent Atlas routes
// render only their own range control and keep 90d.
const freshDefaultKey = () => (
  typeof window !== 'undefined' && window.location.pathname.startsWith('/security-atlas')
    ? '30d'
    : DEFAULT_KEY
);

const PRESET_DAYS = { '1d': 1, '7d': 7, '30d': 30, '90d': 90 };

// Atlas preset keys <-> the shared store's (backend) preset vocabulary.
const KEY_TO_PRESET = {
  '1d': 'last_24_hours',
  '7d': 'last_7_days',
  '30d': 'last_30_days',
  '90d': 'last_90_days',
};
const PRESET_TO_KEY = Object.fromEntries(
  Object.entries(KEY_TO_PRESET).map(([key, preset]) => [preset, key]),
);

const presetToRange = (key) => {
  const days = PRESET_DAYS[key];
  if (!days) return { start_time: null, end_time: null };
  const now = new Date();
  const start = new Date(now.getTime() - days * 86_400_000);
  return { start_time: start.toISOString(), end_time: now.toISOString() };
};

const daysFromRange = (range) => {
  if (!range?.start_time || !range?.end_time) return 7;
  const ms = new Date(range.end_time).getTime() - new Date(range.start_time).getTime();
  return Math.max(1, Math.ceil(ms / 86_400_000));
};

/** A shared-store selection (or null) as Atlas state, defaulting per route. */
const resolveSelection = (selection) => {
  if (selection?.preset === 'custom') {
    return {
      rangeKey: 'custom',
      dateRange: { start_time: selection.start_time, end_time: selection.end_time },
    };
  }
  const key = PRESET_TO_KEY[selection?.preset] || freshDefaultKey();
  return { rangeKey: key, dateRange: presetToRange(key) };
};

// Preset ranges are relative to now, so presetToRange() returns a new end_time
// on every call. Compare presets by key and custom ranges by their bounds, or
// state would churn — and every bundle keyed on [rangeKey, dateRange] refetch.
const sameSelection = (a, b) => a.rangeKey === b.rangeKey && (
  a.rangeKey !== 'custom'
  || (
    a.dateRange?.start_time === b.dateRange?.start_time
    && a.dateRange?.end_time === b.dateRange?.end_time
  )
);

export function getStoredDays() {
  const { rangeKey, dateRange } = resolveSelection(readStoredTimeRange());
  if (rangeKey === 'custom') return daysFromRange(dateRange);
  return PRESET_DAYS[rangeKey] || 7;
}

export default function useAtlasTimeRange() {
  const scope = useAtlasTimeRangeScope();
  const [searchParams, setSearchParams] = useSearchParams();
  // Latest params for setRange, without re-creating the callback per navigation.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // A URL override (persisted on arrival) wins; otherwise the stored selection.
  // Both are memoized on their serialized value, so `effective` only changes
  // identity when the selection really changes.
  const override = useUrlTimeRangeOverride(searchParams);
  const stored = useStoredTimeRange();
  const effective = override || stored;

  const [{ rangeKey, dateRange }, setState] = useState(() => resolveSelection(effective));

  // Follow a change made elsewhere: the Header filter, another Atlas control on
  // this page, another tab, or navigation to a URL carrying a different range.
  // While setRange's URL cleanup is still in flight, `effective` is still the
  // old override and unchanged, so this cannot revert a live pill selection.
  useEffect(() => {
    // A module scope owns the window; never seed the app-wide store from here.
    if (scope) return;
    const next = resolveSelection(effective);
    setState((current) => (sameSelection(current, next) ? current : next));
    if (!effective) {
      writeStoredTimeRange(KEY_TO_PRESET[next.rangeKey] || KEY_TO_PRESET[freshDefaultKey()]);
    }
  }, [effective, scope]);

  const setRange = useCallback((key, range) => {
    if (scope) {
      const scopedCustom = key === 'custom' && Boolean(range?.start_time && range?.end_time);
      const scopedKey = scopedCustom || PRESET_DAYS[key] ? key : freshDefaultKey();
      scope.setRange(scopedKey, scopedCustom ? range : presetToRange(scopedKey));
      return;
    }
    const isCustom = key === 'custom' && Boolean(range?.start_time && range?.end_time);
    const safeKey = isCustom || PRESET_DAYS[key] ? key : freshDefaultKey();
    const safeRange = isCustom ? range : presetToRange(safeKey);
    setState({ rangeKey: safeKey, dateRange: safeRange });
    writeStoredTimeRange(isCustom
      ? { preset: 'custom', start_time: safeRange.start_time, end_time: safeRange.end_time }
      : KEY_TO_PRESET[safeKey]);
    // The choice now lives in storage. A range the URL arrived with is stale and
    // would outrank it, so drop it (and the legacy days param) — never add one.
    if (URL_RANGE_PARAMS.some((param) => searchParamsRef.current.has(param))) {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        URL_RANGE_PARAMS.forEach((param) => next.delete(param));
        return next;
      }, { replace: true });
    }
  }, [setSearchParams, scope]);

  // Back-compat for any caller still using setDays(n) directly.
  const setDays = useCallback((n) => {
    const v = Number.isFinite(n) && n > 0 ? Math.floor(n) : 7;
    const presetKey = Object.entries(PRESET_DAYS).find(([, d]) => d === v)?.[0];
    if (presetKey) setRange(presetKey, presetToRange(presetKey));
    else {
      // No matching preset → emit a synthetic custom range of N days.
      const now = new Date();
      const start = new Date(now.getTime() - v * 86_400_000);
      setRange('custom', { start_time: start.toISOString(), end_time: now.toISOString() });
    }
  }, [setRange]);

  const scoped = scope ? {
    rangeKey: scope.rangeKey === 'custom' || PRESET_DAYS[scope.rangeKey] ? scope.rangeKey : '30d',
    dateRange: scope.dateRange || presetToRange('30d'),
  } : null;
  const activeKey = scoped ? scoped.rangeKey : rangeKey;
  const activeRange = scoped ? scoped.dateRange : dateRange;
  const days = activeKey === 'custom' ? daysFromRange(activeRange) : PRESET_DAYS[activeKey] || 7;

  // For custom date ranges, pass absolute timestamps so the backend can apply
  // an exact filter rather than a relative "now - N days" window.
  const dateParams = scope?.dateParams || {
    days,
    ...(activeKey === 'custom' && activeRange?.start_time && activeRange?.end_time
      ? { start_time: activeRange.start_time, end_time: activeRange.end_time }
      : {}),
  };

  return {
    rangeKey: activeKey,
    dateRange: activeRange,
    setRange,
    days,
    setDays,
    dateParams,
  };
}

/**
 * The window the Atlas time range selects, read-only: the module scope's, else the URL override,
 * else the stored selection, else the fresh default - the same `dateParams` useAtlasTimeRange
 * returns, but it never writes the app-wide store. For a reader that only needs a default window
 * (src/metrics' useMetrics): seeding the store from there changed the range of any page that keeps
 * its own window off the same store (the Cloud overview went from 30 to 90 days, 2026-10-17).
 */
export function useAtlasDateParams() {
  const scope = useAtlasTimeRangeScope();
  const [searchParams] = useSearchParams();
  const override = useUrlTimeRangeOverride(searchParams);
  const stored = useStoredTimeRange();
  const { rangeKey, dateRange } = scope
    ? {
      rangeKey: scope.rangeKey === 'custom' || PRESET_DAYS[scope.rangeKey] ? scope.rangeKey : '30d',
      dateRange: scope.dateRange || presetToRange('30d'),
    }
    : resolveSelection(override || stored);
  const days = rangeKey === 'custom' ? daysFromRange(dateRange) : PRESET_DAYS[rangeKey] || 7;
  return scope?.dateParams || {
    days,
    ...(rangeKey === 'custom' && dateRange?.start_time && dateRange?.end_time
      ? { start_time: dateRange.start_time, end_time: dateRange.end_time }
      : {}),
  };
}

/** Display label for a window. */
export function timeRangeLabel(days) {
  if (days === 7)  return 'Last 7 days';
  if (days === 30) return 'Last 30 days';
  if (days === 90) return 'Last 90 days';
  if (days === 1)  return 'Last 24 hours';
  return `Last ${days} days`;
}
